import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Check, ExternalLink, Loader2, X } from "lucide-react";
import { Btn, Section, cellCls } from "@/components/account/Shell";
import { Cell, Filters, IconBtn, PillRow, Sheet, Tag, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError } from "@/lib/api";
import { themeOf } from "@/lib/mall";

const FILTERS = [["pending_review", "Applications"], ["active", "Open shops"], ["suspended", "Suspended"], ["rejected", "Rejected"], ["", "All"]];
const TIERS = [["kiosk", "Starter"], ["storefront", "Storefront"], ["department", "Pro Shop"], ["standalone", "Virtual Brick & Mortar"]];
const COLS = [
    { key: "shop", label: "Shop", sub: "name · city", w: "minmax(200px,1.4fr)" },
    { key: "tier", label: "Space", sub: "change the plan here", w: "170px" },
    { key: "status", label: "Status", sub: "application · open · suspended", w: "128px" },
    { key: "flags", label: "Badges", sub: "verified · designers", w: "150px" },
    { key: "owner", label: "Owner", sub: "login · phone", w: "minmax(200px,1.3fr)" },
    { key: "notify", label: "Notifications go to", sub: "their inbox, or the owner's login", w: "minmax(180px,1.1fr)" },
    { key: "sells", label: "What they sell", w: "minmax(160px,1fr)" },
    { key: "actions", label: "Actions", sub: "approve · suspend · verify · visit", w: "220px" },
];

export default function ShopsAdmin() {
    const [status, setStatus] = useState("pending_review");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = useCallback(() => {
        api.get("/platform/stores", { params: status ? { status } : {} })
            .then(({ data }) => setData(data)).catch(() => setData({ stores: [], pending: 0 }));
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const review = async (slug, patch, note) => {
        setBusy(slug);
        try { await api.post(`/platform/stores/${slug}/review`, patch); toast.success(note); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(null); }
    };

    return (
        <div data-testid="admin-shops">
            <Filters testid="admin-shops-filters">
                <PillRow label="Show" testid="shops-filter" value={status} onPick={setStatus}
                    choices={FILTERS.map(([key, label]) => ({ key, label, count: key === "pending_review" && data?.pending ? data.pending : undefined }))} />
            </Filters>
            <div className="mt-3">
                {!data ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#4a4a4a]" /></div> : (
                    <Section title={`${data.stores.length} shops`} pad={false} testid="admin-shops-sheet"
                        note="applications land here the moment someone rents a space">
                        <Sheet cols={COLS} rows={data.stores} rowKey={(s) => s.slug} testid="shops-sheet" minWidth={1400}
                            rowTestid={(s) => `admin-shop-${s.slug}`} empty="Nothing here. Applications land in this tab the moment someone rents a space."
                            rowCls={(s) => (s.status === "pending_review" ? "border-l-4 border-l-[#b54708]" : "border-l-4 border-l-transparent")}
                            cells={(s) => {
                                const t = themeOf(s);
                                return {
                                    shop: (
                                        <Cell>
                                            <span className="h-3 w-3 shrink-0 border border-black/10" style={{ background: t.primary }} aria-hidden />
                                            <Two a={s.name} b={[s.city || "no city", s.tagline].filter(Boolean).join(" · ")} />
                                        </Cell>
                                    ),
                                    tier: (
                                        <Cell>
                                            <select value={s.tier} data-testid={`shop-tier-${s.slug}`} disabled={busy === s.slug}
                                                onChange={(e) => review(s.slug, { decision: "tier", tier: e.target.value }, "Space updated")}
                                                className={`${cellCls} w-full py-px text-left text-[11px] font-semibold`}>
                                                {TIERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                            </select>
                                        </Cell>
                                    ),
                                    status: <Cell><Tag status={s.status} testid={`shop-status-${s.slug}`} /></Cell>,
                                    flags: (
                                        <Cell className="flex-wrap">
                                            {s.verified && <Tag status="active" testid={`shop-verified-${s.slug}`}><BadgeCheck size={9} /> verified</Tag>}
                                            {s.designers_enabled && <Tag status="booked">designers</Tag>}
                                            {!s.verified && !s.designers_enabled && <Txt sub>—</Txt>}
                                        </Cell>
                                    ),
                                    owner: <Cell><Two a={s.owner_name || s.owner_email} b={[s.owner_email, s.phone].filter(Boolean).join(" · ")} /></Cell>,
                                    notify: <Cell><Txt>{s.notification_email || s.owner_email}</Txt></Cell>,
                                    sells: <Cell><Txt sub title={s.what_you_sell}>{s.what_you_sell || "—"}</Txt></Cell>,
                                    actions: (
                                        <Cell className="justify-end">
                                            {s.status !== "active" && (
                                                <Btn tone="solid" onClick={() => review(s.slug, { decision: "approve" }, `${s.name} is open`)} disabled={busy === s.slug} testid={`shop-approve-${s.slug}`}>
                                                    <Check size={10} /> Approve
                                                </Btn>
                                            )}
                                            {s.status === "active" && (
                                                <Btn tone="danger" onClick={() => review(s.slug, { decision: "suspend" }, `${s.name} suspended`)} disabled={busy === s.slug} testid={`shop-suspend-${s.slug}`}>
                                                    <X size={10} /> Suspend
                                                </Btn>
                                            )}
                                            <Btn onClick={() => review(s.slug, { decision: "verify", verified: !s.verified }, s.verified ? "Badge removed" : "Verified")}
                                                disabled={busy === s.slug} testid={`shop-verify-${s.slug}`}>
                                                <BadgeCheck size={10} /> {s.verified ? "Unverify" : "Verify"}
                                            </Btn>
                                            <IconBtn href={`/stores/${s.slug}`} title="Visit the shop" testid={`shop-visit-${s.slug}`}><ExternalLink size={12} /></IconBtn>
                                        </Cell>
                                    ),
                                };
                            }} />
                    </Section>
                )}
            </div>
        </div>
    );
}
